import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search, CheckCircle, XCircle, FileText, Loader2, RefreshCw,
  Calendar, Clock, AlertTriangle, ExternalLink, Filter, X, Printer, FileSpreadsheet,
} from 'lucide-react';
import { OCNumber } from '../lib/ocFormat';
import { exportToCSV, printTable } from '../lib/printUtils';
import { DteDocsActions } from '../components/dte/DteDocsActions'; // Iter 235.d
import { ProveedorHistorialModal } from '../components/dte/ProveedorHistorialModal'; // Iter 235.e
import { StickyHScroll } from '../components/ui/sticky-hscroll'; // Iter 235.g

const fmt = (num) => Math.round(num || 0).toLocaleString('es-CL');

const isOverdue = (fechaVenc) => {
  if (!fechaVenc) return false;
  try {
    return new Date(fechaVenc) < new Date();
  } catch { return false; }
};

const daysUntil = (fechaVenc) => {
  if (!fechaVenc) return null;
  try {
    const diff = Math.ceil((new Date(fechaVenc) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  } catch { return null; }
};

// Date range presets
const getDateRange = (preset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const formatDate = (d) => d.toISOString().split('T')[0];
  
  switch (preset) {
    case 'hoy':
      return { desde: formatDate(today), hasta: formatDate(today) };
    case 'ayer': {
      const ayer = new Date(today);
      ayer.setDate(ayer.getDate() - 1);
      return { desde: formatDate(ayer), hasta: formatDate(ayer) };
    }
    case '7dias': {
      const hace7 = new Date(today);
      hace7.setDate(hace7.getDate() - 7);
      return { desde: formatDate(hace7), hasta: formatDate(today) };
    }
    case '30dias': {
      const hace30 = new Date(today);
      hace30.setDate(hace30.getDate() - 30);
      return { desde: formatDate(hace30), hasta: formatDate(today) };
    }
    case 'este_mes': {
      const inicioMes = new Date(today.getFullYear(), today.getMonth(), 1);
      return { desde: formatDate(inicioMes), hasta: formatDate(today) };
    }
    case 'mes_anterior': {
      const inicioMesAnterior = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const finMesAnterior = new Date(today.getFullYear(), today.getMonth(), 0);
      return { desde: formatDate(inicioMesAnterior), hasta: formatDate(finMesAnterior) };
    }
    default:
      return { desde: '', hasta: '' };
  }
};

export default function SeguimientoFacturas() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Iter 235.d (Carlos): Buscador global por folio/proveedor/RUT
  const [globalQuery, setGlobalQuery] = useState('');
  const [globalLoading, setGlobalLoading] = useState(false);
  const [globalResults, setGlobalResults] = useState(null);
  const [globalError, setGlobalError] = useState(null);
  const [provHistModal, setProvHistModal] = useState({ open: false, rut: null }); // Iter 235.e

  const doGlobalSearch = async () => {
    const q = (globalQuery || '').trim();
    if (q.length < 2) {
      setGlobalError('Ingrese al menos 2 caracteres');
      setGlobalResults(null);
      return;
    }
    setGlobalLoading(true);
    setGlobalError(null);
    try {
      const res = await api.get('/contabilidad/buscar-factura-global', { params: { q } });
      setGlobalResults(res.data);
    } catch (err) {
      setGlobalError(err.response?.data?.detail || 'Error en la búsqueda');
      setGlobalResults(null);
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleGlobalKey = (e) => {
    if (e.key === 'Enter') doGlobalSearch();
  };

  const clearGlobalSearch = () => {
    setGlobalQuery('');
    setGlobalResults(null);
    setGlobalError(null);
  };
  
  // Filters
  const [datePreset, setDatePreset] = useState('30dias');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [otFilter, setOtFilter] = useState('');

  // Initialize with default date range (last 30 days)
  useEffect(() => {
    const { desde, hasta } = getDateRange('30dias');
    setFechaDesde(desde);
    setFechaHasta(hasta);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      if (otFilter && !isNaN(parseInt(otFilter))) params.ot_numero = parseInt(otFilter);
      
      const res = await api.get('/contabilidad/seguimiento-facturas', { params });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [fechaDesde, fechaHasta, otFilter]);

  // Auto-fetch on initial load and when date range changes via preset
  useEffect(() => {
    if (fechaDesde && fechaHasta) {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaDesde, fechaHasta]);

  const handlePresetChange = (value) => {
    setDatePreset(value);
    if (value === 'personalizado') {
      // Keep current dates, user will select manually
      return;
    }
    const { desde, hasta } = getDateRange(value);
    setFechaDesde(desde);
    setFechaHasta(hasta);
  };

  const handleClearOT = () => {
    setOtFilter('');
    setTimeout(() => fetchData(), 100);
  };

  const handleOtKeyPress = (e) => {
    if (e.key === 'Enter') {
      fetchData();
    }
  };

  // Carlos (10/05/2026): imprimir y exportar Seguimiento Facturas
  const imprimirSegFacturas = () => {
    const facts = data?.facturas || [];
    if (!facts.length) { return; }
    printTable({
      title: 'Seguimiento de Facturas',
      subtitle: `${facts.length} facturas · ${fechaDesde || ''} → ${fechaHasta || ''} · ${new Date().toLocaleString('es-CL')}`,
      summary: data?.kpis ? [
        { label: 'Total facturas', value: data.kpis.total ?? facts.length },
        { label: 'Vencidas', value: data.kpis.vencidas ?? 0, color: '#b91c1c' },
        { label: 'Por vencer', value: data.kpis.por_vencer ?? 0, color: '#b45309' },
        { label: 'Pagadas', value: data.kpis.pagadas ?? 0, color: '#047857' },
      ] : [{ label: 'Total facturas', value: facts.length }],
      columns: [
        { key: 'numero', label: 'Folio', mono: true, align: 'center' },
        { key: 'proveedor', label: 'Proveedor', format: (v, r) => v || r.proveedor_nombre || '' },
        { key: 'numero_oc', label: 'OC', mono: true, align: 'center' },
        { key: 'fecha', label: 'Emisión' },
        { key: 'fecha_vencimiento', label: 'Vencimiento' },
        { key: 'total', label: 'Total', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
        { key: 'saldo', label: 'Saldo', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
        { key: 'estado', label: 'Estado', align: 'center', format: (v) => (v || '').toUpperCase() },
      ],
      rows: facts,
    });
  };

  const exportarSegFacturasExcel = () => {
    const facts = data?.facturas || [];
    if (!facts.length) { return; }
    exportToCSV({
      filename: 'seguimiento_facturas',
      title: 'CHIRINO STEEL · Seguimiento de Facturas',
      summary: [
        { label: 'Total facturas', value: facts.length },
        { label: 'Período', value: `${fechaDesde || '-'} → ${fechaHasta || '-'}` },
        { label: 'Filtro proveedor', value: 'Todos' },
        { label: 'Filtro OT', value: otFilter || 'Todas' },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'numero', label: 'Folio' },
        { key: 'tipo_dte', label: 'Tipo DTE' },
        { key: 'proveedor', label: 'Proveedor', format: (v, r) => v || r.proveedor_nombre || '' },
        { key: 'rut_proveedor', label: 'RUT' },
        { key: 'numero_oc', label: 'N. OC' },
        { key: 'ot', label: 'OT' },
        { key: 'fecha', label: 'Fecha emisión' },
        { key: 'fecha_vencimiento', label: 'Fecha vencimiento' },
        { key: 'dias_vencido', label: 'Días vencido' },
        { key: 'total', label: 'Total (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'saldo', label: 'Saldo (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'estado', label: 'Estado' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows: facts,
    });
  };

  return (
    <div className="space-y-5" data-testid="seguimiento-facturas-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="w-6 h-6 text-blue-600" />
          Seguimiento de Facturas
        </h1>
        <p className="text-sm text-muted-foreground">
          Verificacion de facturas recepcionadas contra DTEs y condiciones de pago
        </p>
      </div>

      {/* Iter 235.d (Carlos): Buscador global por folio / proveedor / RUT */}
      <div className="bg-white border-2 border-indigo-200 rounded-lg p-4 shadow-sm" data-testid="global-search-section">
        <div className="flex items-center gap-2 mb-2">
          <Search className="w-4 h-4 text-indigo-600" />
          <h2 className="text-sm font-semibold text-indigo-900">Buscar factura por folio, proveedor o RUT</h2>
        </div>
        <p className="text-[11px] text-gray-500 mb-3">
          Encuentra cualquier factura en el sistema (DTE recibido, contabilizada, en pago, pagada o eliminada).
          Cruza datos de <code className="bg-slate-100 px-1 rounded">dte_documentos</code>, <code className="bg-slate-100 px-1 rounded">fc_factura_compra</code>,
          <code className="bg-slate-100 px-1 rounded">contabilizadas_oc</code> y <code className="bg-slate-100 px-1 rounded">alegra_bills</code>.
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[280px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={globalQuery}
              onChange={(e) => setGlobalQuery(e.target.value)}
              onKeyDown={handleGlobalKey}
              placeholder="Ej: 13013815 · MercadoLibre · 77398220-1"
              className="pl-9 pr-9 h-9"
              data-testid="global-search-input"
            />
            {globalQuery && (
              <button
                onClick={clearGlobalSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                data-testid="global-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            onClick={doGlobalSearch}
            disabled={globalLoading || globalQuery.trim().length < 2}
            className="h-9 bg-indigo-600 hover:bg-indigo-700"
            data-testid="global-search-btn"
          >
            {globalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-1.5">Buscar</span>
          </Button>
        </div>

        {globalError && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> {globalError}
          </div>
        )}

        {globalResults && (
          <div className="mt-3" data-testid="global-search-results">
            <p className="text-[11px] text-gray-600 mb-2">{globalResults.mensaje}</p>
            {globalResults.resultados.length > 0 && (
              <StickyHScroll className="border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600 text-[10px] uppercase">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Folio</th>
                      <th className="px-2 py-1.5 text-left">Proveedor / RUT</th>
                      <th className="px-2 py-1.5 text-left">F. Emis · Venc</th>
                      <th className="px-2 py-1.5 text-right">Total</th>
                      <th className="px-2 py-1.5 text-center">OC</th>
                      <th className="px-2 py-1.5 text-center">Recep</th>
                      <th className="px-2 py-1.5 text-center">Estado</th>
                      <th className="px-2 py-1.5 text-center">Docs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {globalResults.resultados.map((r) => {
                      const colorMap = {
                        emerald: 'bg-emerald-100 text-emerald-800 border-emerald-300',
                        amber: 'bg-amber-100 text-amber-800 border-amber-300',
                        blue: 'bg-blue-100 text-blue-800 border-blue-300',
                        red: 'bg-red-100 text-red-800 border-red-300',
                        gray: 'bg-gray-100 text-gray-700 border-gray-300',
                      };
                      return (
                        <tr key={`${r.folio}-${r.rut_emisor}`} className="border-t hover:bg-slate-50">
                          <td className="px-2 py-1.5 font-mono font-bold">{r.folio}</td>
                          <td className="px-2 py-1.5">
                            <div className="font-medium truncate max-w-[280px]" title={`${r.proveedor}\nRUT: ${r.rut_emisor}`}>{r.proveedor}</div>
                            <div className="text-[10px] text-gray-500 flex items-center gap-1.5">
                              <button
                                className="font-mono text-indigo-600 hover:underline"
                                onClick={() => setProvHistModal({ open: true, rut: r.rut_emisor })}
                                title="Ver historial del proveedor"
                                data-testid={`hist-prov-${r.folio}`}
                              >
                                {r.rut_emisor}
                              </button>
                              {r.comuna && <span>📍 {r.comuna}</span>}
                            </div>
                          </td>
                          <td className="px-2 py-1.5 text-[10px] text-gray-600">
                            <div>{r.fecha_emision || '—'}</div>
                            {r.fecha_vencimiento && (
                              <div className={isOverdue(r.fecha_vencimiento) ? 'text-red-600 font-medium' : 'text-amber-700'}>
                                ⏰ {r.fecha_vencimiento}
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums">${fmt(r.total)}</td>
                          <td className="px-2 py-1.5 text-center text-[10px]">
                            {r.orden_compra ? <span className="font-mono text-green-700 font-medium">{r.orden_compra}</span> : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-2 py-1.5 text-center text-[10px]">
                            {r.numero_recepcion ? <span className="font-mono text-purple-700">{r.numero_recepcion}</span> : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <span className={`inline-flex px-1.5 py-0.5 rounded border text-[9px] font-bold whitespace-nowrap ${colorMap[r.estado_color] || colorMap.gray}`}>
                              {r.estado_label}
                            </span>
                            {r.en_nomina && (
                              <div className="text-[8px] text-blue-600 mt-0.5">en nómina</div>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <DteDocsActions
                              folio={r.folio}
                              rutEmisor={r.rut_emisor}
                              numeroOc={r.orden_compra}
                              numeroRecepcion={r.numero_recepcion}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </StickyHScroll>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Filter className="w-4 h-4" />
          Filtros
        </div>
        
        <div className="flex flex-wrap gap-4 items-end">
          {/* Date preset selector */}
          <div className="w-48">
            <label className="block text-xs font-medium mb-1 text-gray-600">Periodo</label>
            <Select value={datePreset} onValueChange={handlePresetChange}>
              <SelectTrigger data-testid="date-preset-select">
                <SelectValue placeholder="Seleccionar periodo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hoy">Hoy</SelectItem>
                <SelectItem value="ayer">Ayer</SelectItem>
                <SelectItem value="7dias">Ultimos 7 dias</SelectItem>
                <SelectItem value="30dias">Ultimos 30 dias</SelectItem>
                <SelectItem value="este_mes">Este mes</SelectItem>
                <SelectItem value="mes_anterior">Mes anterior</SelectItem>
                <SelectItem value="personalizado">Personalizado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Custom date range */}
          {datePreset === 'personalizado' && (
            <>
              <div className="w-40">
                <label className="block text-xs font-medium mb-1 text-gray-600">Desde</label>
                <Input
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  data-testid="fecha-desde-input"
                />
              </div>
              <div className="w-40">
                <label className="block text-xs font-medium mb-1 text-gray-600">Hasta</label>
                <Input
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  data-testid="fecha-hasta-input"
                />
              </div>
            </>
          )}
          
          {/* OT Filter (optional) */}
          <div className="w-32">
            <label className="block text-xs font-medium mb-1 text-gray-600">N. OT (opcional)</label>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                placeholder="Ej: 3457"
                value={otFilter}
                onChange={(e) => setOtFilter(e.target.value)}
                onKeyPress={handleOtKeyPress}
                data-testid="ot-filter-input"
                className="text-sm"
              />
              {otFilter && (
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleClearOT}>
                  <X className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
          
          {/* Search button */}
          <Button onClick={fetchData} disabled={loading} data-testid="buscar-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
            Buscar
          </Button>
          
          {/* Refresh */}
          <Button variant="outline" onClick={fetchData} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" size="sm" onClick={imprimirSegFacturas} title="Imprimir listado" data-testid="btn-print-seg-facturas">
            <Printer className="w-3.5 h-3.5 mr-1" /> Imprimir
          </Button>
          <Button variant="outline" size="sm" onClick={exportarSegFacturasExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel" data-testid="btn-excel-seg-facturas">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
          </Button>
        </div>
        
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>

      {data && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-white border rounded p-3 text-center">
              <p className="text-xs text-muted-foreground">Total Facturas</p>
              <p className="text-xl font-bold" data-testid="total-facturas">{data.resumen?.total}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded p-3 text-center">
              <p className="text-xs text-green-700">Con DTE Confirmado</p>
              <p className="text-xl font-bold text-green-700" data-testid="con-dte">{data.resumen?.con_dte}</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-center">
              <p className="text-xs text-yellow-700">Sin DTE</p>
              <p className="text-xl font-bold text-yellow-700" data-testid="sin-dte">{data.resumen?.sin_dte}</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-center">
              <p className="text-xs text-blue-700">Monto Total</p>
              <p className="text-lg font-bold text-blue-700" data-testid="monto-total">
                ${fmt(data.facturas?.reduce((sum, f) => sum + (f.total_neto || 0), 0))}
              </p>
            </div>
          </div>

          {/* Table */}
          {data.facturas?.length > 0 ? (
            <div className="border rounded-lg">
              <StickyHScroll>
                <table className="w-full text-xs" data-testid="facturas-table">
                  <thead className="sticky top-14 lg:top-0 z-20">
                    <tr className="bg-gray-800 text-white text-left">
                      <th className="p-2">N. Factura</th>
                      <th className="p-2">N. OC</th>
                      <th className="p-2">OT</th>
                      <th className="p-2">Proveedor</th>
                      <th className="p-2 text-right">Total Neto</th>
                      <th className="p-2 text-center">DTE</th>
                      <th className="p-2 text-center">Fecha Emision</th>
                      <th className="p-2 text-center">Condicion Pago</th>
                      <th className="p-2 text-center">Fecha Vencimiento</th>
                      <th className="p-2 text-center">Estado</th>
                      <th className="p-2 text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.facturas.map((f, idx) => {
                      const days = daysUntil(f.fecha_vencimiento);
                      const overdue = isOverdue(f.fecha_vencimiento);
                      return (
                        <tr
                          key={`${f.docmto}-${f.numero_oc}-${idx}`}
                          className={`border-t transition-colors ${
                            overdue ? 'bg-red-50' :
                            f.dte_encontrado ? 'bg-green-50' :
                            idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          }`}
                          data-testid={`factura-row-${f.docmto}`}
                        >
                          <td className="p-2 font-mono font-bold text-blue-700">{f.docmto}</td>
                          <td className="p-2 font-mono"><OCNumber oc={f} size="sm" /></td>
                          <td className="p-2 font-mono text-gray-600">{f.ot_numero || '-'}</td>
                          <td className="p-2 truncate max-w-[180px]" title={f.nombre_proveedor}>{f.nombre_proveedor || f.rut_proveedor}</td>
                          <td className="p-2 text-right font-mono">${fmt(f.total_neto)}</td>
                          <td className="p-2 text-center">
                            {f.dte_encontrado ? (
                              <span className="inline-flex items-center gap-0.5 text-green-700" title={`Emisor: ${f.dte_emisor} | Total DTE: $${fmt(f.dte_total)}`}>
                                <CheckCircle className="w-4 h-4" />
                                <span className="text-[10px]">Si</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-0.5 text-yellow-600">
                                <XCircle className="w-4 h-4" />
                                <span className="text-[10px]">No</span>
                              </span>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            {f.fecha_emision ? (
                              <span className="inline-flex items-center gap-1 text-[11px]">
                                <Calendar className="w-3 h-3 text-blue-500" />
                                {f.fecha_emision}
                              </span>
                            ) : <span className="text-gray-300">-</span>}
                          </td>
                          <td className="p-2 text-center">
                            {f.condicion_pago ? (
                              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-[10px]">
                                {f.condicion_pago}
                              </span>
                            ) : (
                              <span className="text-gray-300 text-[10px]">Sin info</span>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            {f.fecha_vencimiento ? (
                              <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${overdue ? 'text-red-700' : 'text-gray-700'}`}>
                                <Clock className={`w-3 h-3 ${overdue ? 'text-red-500' : 'text-gray-400'}`} />
                                {f.fecha_vencimiento}
                              </span>
                            ) : <span className="text-gray-300">-</span>}
                          </td>
                          <td className="p-2 text-center">
                            {f.fecha_vencimiento ? (
                              overdue ? (
                                <span className="inline-flex items-center gap-0.5 bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-[10px] font-medium">
                                  <AlertTriangle className="w-3 h-3" /> Vencida ({Math.abs(days)}d)
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-0.5 bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-[10px] font-medium">
                                  <CheckCircle className="w-3 h-3" /> {days}d
                                </span>
                              )
                            ) : !f.dte_encontrado ? (
                              <span className="text-[10px] text-gray-400">Sin DTE</span>
                            ) : !f.condicion_pago ? (
                              <span className="text-[10px] text-gray-400">Sin cond. pago</span>
                            ) : (
                              <span className="text-[10px] text-gray-400">-</span>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {/* View OC */}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                title="Ver Orden de Compra"
                                onClick={() => window.open(`/compras/ordenes-compra?oc=${f.numero_oc}`, '_blank')}
                                data-testid={`ver-oc-${f.docmto}`}
                              >
                                <ExternalLink className="w-3 h-3 text-blue-600" />
                              </Button>
                              {/* View DTE/Invoice - only if found */}
                              {f.dte_encontrado && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  title="Ver DTE/Factura"
                                  onClick={() => window.open(`/dte/manager?folio=${f.docmto}`, '_blank')}
                                  data-testid={`ver-dte-${f.docmto}`}
                                >
                                  <FileText className="w-3 h-3 text-green-600" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </StickyHScroll>
              <div className="p-2 bg-gray-50 border-t text-xs text-gray-500 text-right">
                Mostrando {data.facturas.length} facturas
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p className="text-muted-foreground">No se encontraron facturas para el periodo seleccionado</p>
            </div>
          )}
        </>
      )}

      {!data && !loading && (
        <div className="text-center py-16 bg-gray-50 rounded-lg">
          <Search className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-600">Cargando datos...</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Seleccione un periodo para ver el seguimiento de facturas
          </p>
        </div>
      )}

      {/* Iter 235.e (Carlos): Modal historial proveedor */}
      <ProveedorHistorialModal
        open={provHistModal.open}
        rut={provHistModal.rut}
        onClose={() => setProvHistModal({ open: false, rut: null })}
      />
    </div>
  );
}
